import { motion } from 'framer-motion'
import SectionWrapper from '../layout/SectionWrapper'
import AnimatedCounter from '../ui/AnimatedCounter'
import { stats, compatibilityScores } from '../../data/stats'

export default function StatsSection() {
  return (
    <SectionWrapper
      id="stats"
      gradient="from-dark-primary via-primary-pink/5 to-dark-primary"
    >
      {/* Header */}
      <motion.div className="text-center mb-8 sm:mb-12 lg:mb-16 px-4">
        <motion.span
          className="tag-pill mb-3 sm:mb-4"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <span>📊</span>
          <span>En Chiffres</span>
        </motion.span>

        <motion.h2
          className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-serif font-bold gradient-text mb-2 sm:mb-4"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
        >
          2025 en Chiffres
        </motion.h2>

        <motion.p
          className="text-sm sm:text-base lg:text-lg text-light-pink-100/70 px-4"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
        >
          Chaque chiffre raconte une partie de notre histoire
        </motion.p>
      </motion.div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 md:gap-4 lg:gap-6 mb-8 sm:mb-12 lg:mb-16 px-2 sm:px-0">
        {stats.map((stat, index) => (
          <motion.div
            key={stat.id}
            className="glass-card text-center py-3 sm:py-4 lg:py-6 px-2 sm:px-4"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1 }}
            whileHover={{ y: -5, boxShadow: '0 15px 30px rgba(232, 81, 178, 0.2)' }}
          >
            <span className="text-2xl sm:text-3xl lg:text-4xl block mb-2 sm:mb-3">{stat.emoji}</span>
            <div className={`text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold ${stat.color} mb-1 sm:mb-2`}>
              <AnimatedCounter
                value={stat.value}
                suffix={stat.suffix}
                duration={2}
              />
            </div>
            <span className="text-xs sm:text-sm text-light-pink-100/70 leading-tight block">{stat.label}</span>
          </motion.div>
        ))}
      </div>

      {/* Compatibility Section */}
      <motion.div
        className="max-w-3xl mx-auto px-4 sm:px-6"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <h3 className="text-lg sm:text-xl lg:text-2xl font-serif font-bold text-center text-white mb-4 sm:mb-6 lg:mb-8">
          💕 Score de Compatibilité
        </h3>

        <div className="space-y-3 sm:space-y-4 lg:space-y-6">
          {compatibilityScores.map((score, index) => (
            <motion.div
              key={score.id}
              className="glass-card p-3 sm:p-4 lg:p-6"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15 }}
            >
              <div className="flex items-center justify-between mb-2 sm:mb-3">
                <div className="flex items-center gap-2 sm:gap-3">
                  <span className="text-lg sm:text-xl lg:text-2xl">{score.emoji}</span>
                  <span className="font-medium text-white text-xs sm:text-sm lg:text-base">{score.label}</span>
                </div>
                <span className="text-lg sm:text-xl lg:text-2xl font-bold text-primary-pink">
                  <AnimatedCounter value={score.value} suffix="%" duration={1.5} />
                </span>
              </div>

              {/* Progress Bar */}
              <div className="h-2 sm:h-3 bg-dark-primary rounded-full overflow-hidden">
                <motion.div
                  className="h-full rounded-full"
                  style={{ backgroundColor: score.color }}
                  initial={{ width: 0 }}
                  whileInView={{ width: `${score.value}%` }}
                  viewport={{ once: true }}
                  transition={{ duration: 1.5, delay: index * 0.15, ease: 'easeOut' }}
                />
              </div>
            </motion.div>
          ))}
        </div>

        {/* Summary */}
        <motion.div
          className="text-center mt-6 sm:mt-8"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <span className="inline-block px-4 py-2 sm:px-6 sm:py-3 bg-primary-pink/20 rounded-full text-primary-pink font-bold text-sm sm:text-base lg:text-lg">
            Compatibilité Totale: 99% 💯
          </span>
        </motion.div>
      </motion.div>

      {/* Quote */}
      <motion.blockquote
        className="text-center mt-8 sm:mt-12 lg:mt-16 max-w-2xl mx-auto px-4"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-light-pink-100/80 italic font-serif leading-relaxed">
          "115 jours, 50 dates, 15000 messages
          <br />
          Chaque chiffre raconte notre passage"
        </p>
        <footer className="mt-3 sm:mt-4 text-primary-pink text-xs sm:text-sm">
          — Outro
        </footer>
      </motion.blockquote>
    </SectionWrapper>
  )
}
